import usePaymentTrackingPublicPage from 'components/pages/PaymentTrackingPublicPage/usePaymentTrackingPublicPage';
import AmnisLoader from 'components/ui/AmnisLoader/AmnisLoader';
import {format} from 'date-fns';
import {translate} from 'helpers/localize';
import {findLastIndex, formatIban, formatNumber} from 'helpers/common';
import ArrowIcon from 'components/pages/PaymentTrackingPublicPage/components/Arrow';
import PaymentTimeline from 'components/ui/PaymentTimeline/PaymentTimeline';
import classNames from 'classnames';
import TimeSaveIcon from 'components/pages/PaymentTrackingPublicPage/components/TimeSave';
import Button from 'components/ui/Button';
import WaitingIcon from 'components/ui/Icon/StatusIcons/Waiting';
import PublicPageFooter from 'components/ui/PublicPageFooter/PublicPageFooter';
import WarningIcon from 'components/ui/Icon/StatusIcons/Warning';
import {PATH} from 'constants/common';
import PublicHeader from 'components/layout/Header/PublicHeader';
import AmnisButton from 'components/ui/AmnisButton/AmnisButton';
import styles from './PaymentTrackingPublicPage.module.scss';

const PaymentTrackingPublicPage = () => {
    const {
        data,
        isLoading,
        selectedLanguageParams,
        entries,
        pastEstimatedArrival,
        swiftTracking,
        history,
        handleDownloadMT103,
        isDownloadLoading,
    } = usePaymentTrackingPublicPage();

    const percentage = Math.round(
        ((findLastIndex(entries, e => e.status === 'done') + 1) /
            entries.length) *
            100,
    );

    if (isLoading) {
        return <AmnisLoader />;
    }

    const content = () => {
        if (!data || Array.isArray(data)) {
            return (
                <>
                    <div className={styles.illustration}>
                        <WarningIcon className={styles.pendingIcon} />
                    </div>
                    <h3>
                        We couldn&apos;t find the payment you were looking for.
                    </h3>
                    <div className={styles.paymentData}>
                        <p>
                            It looks like you&apos;ve used a link that
                            doesn&apos;t exist anymore.
                        </p>
                        <p>
                            We recommend contacting your business partner and
                            asking for the new payment tracking link!
                        </p>
                        <p data-isolate>
                            If you need further assistance, feel free to&nbsp;
                            <a
                                href="https://amnistreasury.com/contact-us/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                contact our support team
                            </a>
                            &nbsp;or&nbsp;
                            <a
                                href="https://amnistreasury.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                visit our homepage
                            </a>
                            &nbsp;for more information about amnis.
                        </p>
                    </div>
                </>
            );
        }

        return (
            <>
                {!pastEstimatedArrival ? (
                    <>
                        <div className={styles.illustration}>
                            <WaitingIcon className={styles.pendingIcon} />
                        </div>
                        <h3>Your money is on its way!</h3>
                    </>
                ) : null}
                <h3 data-notranslate>
                    {pastEstimatedArrival
                        ? translate(
                              `The payment from %{businessPartner} has arrived on`,
                              {
                                  businessPartner:
                                      data.business_partner.company_name,
                              },
                          )
                        : translate(
                              `The payment from %{businessPartner} has been set up and is expected to arrive on`,
                              {
                                  businessPartner:
                                      data.business_partner.company_name,
                              },
                          )}
                </h3>
                <div
                    className={classNames(styles.expectedArrival, {
                        [styles.pastEstimatedArrival]: pastEstimatedArrival,
                    })}
                    data-notranslate
                >
                    {data.estimated_time_arrival
                        ? format(
                              new Date(data.estimated_time_arrival),
                              selectedLanguageParams.longFormat,
                              {
                                  locale: selectedLanguageParams.locale,
                              },
                          )
                        : translate('Pending')}
                </div>
                <div className={styles.amountRow} data-notranslate>
                    <span className={styles.amount}>
                        {formatNumber(data.amount)}
                    </span>{' '}
                    {data.currency.split('/').pop()}
                </div>
                <div className={styles.paymentData}>
                    <div className={styles.paymentDataRow}>
                        <div>
                            <p className={styles.label}>from</p>
                            <p data-notranslate>
                                {data.business_partner.company_name}
                            </p>
                            <p data-notranslate className={styles.detail}>
                                {data.business_partner.city},{' '}
                                {translate(
                                    data.business_partner
                                        .country_of_incorporation,
                                )}
                            </p>
                        </div>
                    </div>
                    <div className={styles.paymentDataRow}>
                        <div>
                            <p className={styles.label}>to</p>
                            <p data-notranslate>{data.contact.name}</p>
                            <p data-notranslate className={styles.detail}>
                                {data.contact.city},{' '}
                                {translate(data.contact.country)}
                            </p>
                            <p data-notranslate className={styles.detail}>
                                {data.bank_information.iban
                                    ? formatIban(data.bank_information.iban)
                                    : data.bank_information.account_number}
                            </p>
                        </div>
                    </div>
                    <div className={styles.paymentDataRow}>
                        <div>
                            <p className={styles.label}>
                                {translate('Message from %{businessPartner}', {
                                    businessPartner:
                                        data.business_partner.company_name,
                                })}
                            </p>
                            <p data-notranslate>{data.reference}</p>
                        </div>
                    </div>
                </div>
                {data.is_swift_trackable ? (
                    <AmnisButton
                        variant="default"
                        intent="secondary"
                        startIcon="download"
                        size="xsmall"
                        onClick={handleDownloadMT103}
                        isLoading={isDownloadLoading}
                    >
                        MT103 confirmation
                    </AmnisButton>
                ) : null}
                <h3 className={styles.trackingDetails}>
                    Detailed tracking information
                </h3>
                <div className={styles.progress}>
                    <div className={styles.bar}>
                        <div
                            className={classNames(
                                styles.currentProgress,
                                styles[`width${percentage}`],
                            )}
                        />
                    </div>
                </div>
                <div className={styles.timeline}>
                    <PaymentTimeline
                        size="xl"
                        entries={entries}
                        swiftTracking={swiftTracking}
                    />
                </div>
            </>
        );
    };

    return (
        <div className={styles.container}>
            <PublicHeader />
            <div className={styles.content}>
                <h1>Amnis payment tracking</h1>
                {content()}
            </div>
            {data?.contact?.is_peer_allowed ? (
                <div className={classNames(styles.content, styles.ctaSection)}>
                    <TimeSaveIcon className={styles.illustration} />
                    <div className={styles.text}>
                        <h3>Want to receive your money faster next time?</h3>
                        <p>
                            Get your amnis account today and be paid by Peer to
                            Peer in real-time! Your advantages:
                        </p>
                        <ul>
                            <li>
                                Free, local payments and collections in the UK,
                                CH, US & EU
                            </li>
                            <li>
                                IBANs in 20+ currencies for cross-border
                                transactions
                            </li>
                            <li>
                                24/7 currency exchange at transparent rates
                                (max. 0.4%)
                            </li>
                            <li>
                                Smart expenses with multi-currency debit cards
                                at 0% fees
                            </li>
                            <li>
                                Interest on safeguarded accounts in EUR, USD,
                                GBP & CZK
                            </li>
                        </ul>

                        <Button
                            onClick={() =>
                                window.location.assign(
                                    'https://amnistreasury.com/amnis-webapp-solutions/',
                                )
                            }
                        >
                            Learn more
                        </Button>

                        <Button
                            className={styles.signup}
                            variant="outline"
                            onClick={() => history.push(PATH.REGISTRATION)}
                        >
                            Sign up for amnis now
                        </Button>
                    </div>
                    <div className={styles.arrow} />
                </div>
            ) : null}
            <PublicPageFooter />
        </div>
    );
};

export default PaymentTrackingPublicPage;
